import React, { useState } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Send } from 'lucide-react';
import { completeFirstRunStep } from '@/lib/firstRun';

export const EmailDocButton = ({ docType, refId, docLabel, defaultEmail = '', className = '', style = {} }) => {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/documents/email', {
        doc_type: docType, ref_id: refId, to_email: email.trim(), message,
      });
      toast.success(`${data.doc} emailed to ${data.to}`);
      completeFirstRunStep(api, 'deliver');
      setOpen(false); setMessage('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send email');
    }
    setSending(false);
  };

  return (
    <div className="relative inline-block">
      <button onClick={() => setOpen(!open)} data-testid={`email-doc-btn-${docType}`}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors hover:bg-[rgba(26,76,124,0.08)] ${className}`}
        style={{ color: '#1A4C7C', border: '1px solid rgba(26,76,124,0.3)', ...style }}
        title={`Email the ${docLabel || 'document'} to your client`}>
        <Mail className="w-3.5 h-3.5" />Email to Client
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-40 w-80 p-3.5 rounded-md bg-white shadow-lg space-y-2 text-left"
          style={{ border: '1px solid rgba(136,150,168,0.25)' }} data-testid="email-doc-popover">
          <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: '#5C6B7A', fontFamily: 'Georgia, serif' }}>
            Email {docLabel || 'document'}
          </p>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="client@business.com.au"
            className="w-full px-3 py-2 text-xs rounded-sm border focus:outline-none focus:ring-1 focus:ring-[#1A4C7C]"
            style={{ borderColor: 'rgba(136,150,168,0.3)', color: '#0D1B2A' }} data-testid="email-doc-recipient" />
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Optional note to your client…"
            className="w-full px-3 py-2 text-xs rounded-sm border h-16 focus:outline-none focus:ring-1 focus:ring-[#1A4C7C]"
            style={{ borderColor: 'rgba(136,150,168,0.3)', color: '#0D1B2A' }} data-testid="email-doc-message" />
          <div className="flex gap-2">
            <button onClick={send} disabled={sending || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())}
              className="flex items-center gap-1.5 px-4 py-2 rounded-sm text-xs font-semibold text-white disabled:opacity-40"
              style={{ background: '#1A4C7C' }} data-testid="email-doc-send">
              <Send className="w-3 h-3" />{sending ? 'Sending…' : 'Send'}
            </button>
            <button onClick={() => setOpen(false)} className="px-3 py-2 text-xs font-medium" style={{ color: '#5C6B7A' }}>Cancel</button>
          </div>
          <p className="text-[10px]" style={{ color: '#64748B' }}>The branded PDF is attached automatically and the send is logged to the audit trail.</p>
        </div>
      )}
    </div>
  );
};

export default EmailDocButton;
